// Set of helper functions to facilitate wallet setup
import { UnsupportedChainIdError } from '@web3-react/core'
import { NoBscProviderError } from '@binance-chain/bsc-connector'
import {
  NoEthereumProviderError,
  UserRejectedRequestError as UserRejectedRequestErrorInjected,
} from '@web3-react/injected-connector'
import {
  UserRejectedRequestError as UserRejectedRequestErrorWalletConnect,
  WalletConnectConnector,
} from '@web3-react/walletconnect-connector'
import { ConnectorNames, connectorLocalStorageKey } from 'vvs-uikit'
import { connectorsByName } from 'utils/web3React'
import { ContextData } from 'contexts/Localization/types'
import { ToastContextApi } from 'contexts/ToastsContext/types'
import { BASE_EXPLORER_URLS, BASE_URL } from 'config'
import { ChainId } from 'vvs-sdk'
import { nodes } from './getRpcUrl'

/**
 * Prompt the user to add Cronos as a network on Metamask, or switch to Cronos if the wallet is on a different network
 * @returns {boolean} true if the setup succeeded, false otherwise
 */
export const setupNetwork = async () => {
  const provider = window.ethereum
  if (provider) {
    const chainId = parseInt(process.env.REACT_APP_CHAIN_ID, 10)
    const chainType = chainId === ChainId.MAINNET ? 'Mainnet' : 'Testnet'
    try {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: `0x${chainId.toString(16)}`,
            chainName: `Cronos ${chainType}`,
            nativeCurrency: {
              name: 'CRO',
              symbol: 'cro',
              decimals: 18,
            },
            rpcUrls: nodes,
            blockExplorerUrls: [`${BASE_EXPLORER_URLS[chainId]}/`],
          },
        ],
      })
      return true
    } catch (error) {
      console.error('Failed to setup the network in Metamask:', error)
      return false
    }
  } else {
    console.error("Can't setup the Cronos network on metamask because window.ethereum is undefined")
    return false
  }
}

/**
 * Prompt the user to add a custom token to metamask
 * @param tokenAddress
 * @param tokenSymbol
 * @param tokenDecimals
 * @returns {boolean} true if the token has been added, false otherwise
 */
export const registerToken = async (tokenAddress: string, tokenSymbol: string, tokenDecimals: number) => {
  const tokenAdded = await window.ethereum.request({
    method: 'wallet_watchAsset',
    params: {
      type: 'ERC20',
      options: {
        address: tokenAddress,
        symbol: tokenSymbol,
        decimals: tokenDecimals,
        image: `${BASE_URL}/images/tokens/${tokenAddress}.png`,
      },
    },
  })

  return tokenAdded
}

export const isChainCorrect = async () => {
  const provider = window.ethereum
  if (provider) {
    try {
      const connectedChainId = (await provider.request({
        method: 'eth_chainId',
      })) as unknown as string
      const correctChainId = `0x${parseInt(process.env.REACT_APP_CHAIN_ID, 10).toString(16)}`
      return connectedChainId === correctChainId
    } catch (error) {
      return false
    }
  } else {
    return false
  }
}

export const getActivateErrorHandler = (
  connectorID: ConnectorNames,
  t: (key: string, data?: ContextData) => string,
  toastError: ToastContextApi['toastError'],
  connectionErrorCallback: (error: { connectorID: ConnectorNames } | null) => void,
) => {
  const connector = connectorsByName[connectorID]

  return async (error: Error) => {
    if (
      error instanceof UnsupportedChainIdError &&
      (connectorID === ConnectorNames.Metamask ||
        connectorID === ConnectorNames.CDCDefiWallet ||
        connectorID === ConnectorNames.Injected ||
        connectorID === ConnectorNames.BraveWallet)
    ) {
      connectionErrorCallback({ connectorID })
    } else {
      window.localStorage.removeItem(connectorLocalStorageKey)
      if (error instanceof NoEthereumProviderError || error instanceof NoBscProviderError) {
        toastError(t('Provider Error'), t('No provider was found'))
      } else if (
        error instanceof UserRejectedRequestErrorInjected ||
        error instanceof UserRejectedRequestErrorWalletConnect
      ) {
        if (connector instanceof WalletConnectConnector) {
          const walletConnector = connector as WalletConnectConnector
          walletConnector.walletConnectProvider = null
        }
        toastError(t('Authorization Error'), t('Please authorize to access your account'))
      } else {
        toastError(error.name, error.message)
      }
    }
  }
}
